import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Avatar, IconButton, Card, CardContent, CardActionArea, CircularProgress,
  Alert, Chip
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { listClientRepresentations, setClientRepresentation } from '../../services/personaRepresentationApi';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Client-facing picker for persona representations.
 *
 * Shows a card per allowed rep with avatar, name, one-line description,
 * tags, and a play-voice button. Selecting a card persists via PUT, fires
 * the aimhi:clientUiSettingsChanged event so the resolver refetches, and
 * closes the dialog.
 *
 * Triggered from:
 *   - ChatInterface on first chat (when representationsEnabled and no
 *     existing selection / no platform default).
 *   - "Change appearance" link inside AboutThisAiDialog.
 */
const RepresentationPicker = ({ open, onClose, token, personaKey = null, mandatory = false }) => {
  const [items, setItems] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [defaultKey, setDefaultKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);
  const [playing, setPlaying] = useState(null); // current audio element

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    listClientRepresentations(token, personaKey)
      .then((res) => {
        if (cancelled) return;
        setEnabled(res.enabled !== false);
        setItems(Array.isArray(res.items) ? res.items : []);
        setSelectedKey(res.selectedKey || null);
        setDefaultKey(res.defaultKey || null);
      })
      .catch((e) => setError(e.response?.data?.error || e.message || 'Failed to load representations'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (playing) playing.pause();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token, personaKey]);

  const playSample = async (item) => {
    if (playing) {
      playing.pause();
      setPlaying(null);
    }
    const voice = item?.voice;
    if (!voice?.providerVoiceId) return;
    try {
      // Synthesize the controlled preview text (presentation override → voice
      // override → platform default, resolved server-side) so the preview is
      // relevant to our context rather than the provider's default sample.
      const res = await axios.post(
        `${API_BASE}/tts/generate`,
        { text: voice.previewText || `Hi — this is ${voice.name}.`, voiceId: voice.providerVoiceId },
        { headers: { 'x-auth-token': token } }
      );
      if (!res.data?.audio) return;
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      setPlaying(audio);
      audio.onended = () => setPlaying(null);
      audio.play().catch(() => setPlaying(null));
    } catch (e) {
      setPlaying(null);
    }
  };

  const handleSelect = async (key) => {
    setError('');
    setSaving(true);
    try {
      await setClientRepresentation(token, key);
      setSelectedKey(key);
      window.dispatchEvent(new CustomEvent('aimhi:clientUiSettingsChanged'));
      // Brief delay so the user sees the checkmark before close
      setTimeout(() => {
        if (playing) { playing.pause(); setPlaying(null); }
        onClose();
      }, 300);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to set representation');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (playing) { playing.pause(); setPlaying(null); }
    onClose();
  };

  const closeDisabled = mandatory && !selectedKey;

  return (
    <Dialog open={!!open} onClose={closeDisabled ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Choose your AI's appearance</DialogTitle>
      <DialogContent>
        {!enabled ? (
          <Alert severity="info">Persona representations are not enabled for your account.</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Alert severity="info">No representations are available right now. Your conversation will use the default appearance.</Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              These are AI presentations, not real people. The name, face, and voice are for your comfort —
              the conversation is with an AI. You can change your choice at any time from the chat header.
            </Alert>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
              {items.map((item) => {
                const isSelected = item.key === selectedKey;
                const isDefault = item.key === defaultKey;
                return (
                  <Card
                    key={item.key}
                    variant="outlined"
                    sx={{
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderWidth: isSelected ? 2 : 1,
                      position: 'relative'
                    }}
                  >
                    <CardActionArea onClick={() => handleSelect(item.key)} disabled={saving}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}>
                        <Avatar
                          src={item.avatarImageUrl || undefined}
                          alt={item.displayName}
                          sx={{ width: 96, height: 96, boxShadow: 1 }}
                        >
                          {item.displayName?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.displayName}</Typography>
                        {item.clientDescription && (
                          <Typography variant="body2" color="text.secondary">{item.clientDescription}</Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                          {item.tags?.accent && <Chip size="small" label={item.tags.accent} variant="outlined" />}
                          {item.tags?.ageRange && item.tags.ageRange !== 'unspecified' && (
                            <Chip size="small" label={item.tags.ageRange.replace('_', ' ')} variant="outlined" />
                          )}
                          {item.tags?.gender && item.tags.gender !== 'unspecified' && (
                            <Chip size="small" label={item.tags.gender} variant="outlined" />
                          )}
                          {isDefault && <Chip size="small" label="Default" color="default" />}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                    <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
                      {item.voice?.providerVoiceId ? (
                        <Button
                          size="small"
                          startIcon={playing ? <StopCircleIcon /> : <PlayCircleOutlineIcon />}
                          onClick={(e) => { e.stopPropagation(); playSample(item); }}
                        >
                          Listen
                        </Button>
                      ) : null}
                    </Box>
                    {isSelected && (
                      <CheckCircleIcon
                        color="primary"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    )}
                  </Card>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={closeDisabled || saving}>
          {selectedKey ? 'Done' : 'Skip for now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RepresentationPicker;
